import * as React from "react";
import { DatabaseConnectionModel } from "@app/client";
import { ClientContext, DEFAULT_QUINN_WEEKLY_PROMPT_LIMIT } from "@app/admin/contexts/ClientContext";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { GlobalConstants } from "@app/shared/constants";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { ClientListItem, ClientNetworkBenchmarkingDataRightsEnum, ClientType } from "@app/client/client";
import Features from "@app/feature-flags/features";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { MultiselectItem, MultiselectOnChangeResponse, QESMultiSelect } from "@app/shared/qes-standards/forms/Multiselect";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { ClientService } from "@app/client/clientService";
import { useRequestLimits, UNLIMITED_WEEKLY_QUINN_LIMIT } from "@app/projects/network-assessment/provider-hub/ai-assistant/hooks/useRequestLimits";

interface ClientInfoProps {
  /**
   * Used to disable all fields. This is typically in the case of a deactivated client.
   */
  disabled?: boolean;
}

const clientService = new ClientService();
export const ClientInfo: React.FC<React.PropsWithChildren<ClientInfoProps>> = (props: ClientInfoProps) => {
  const [dbStringList, setDbStringList] = React.useState<DatabaseConnectionModel[]>([]);
  const [regulatorClients, setRegulatorClients] = React.useState<ClientListItem[]>([]);
  const featureFlags = React.useContext(FeatureFlagContext).enabledFeatureFlags;
  const clientContext = React.useContext(ClientContext);
  const { clientRequests } = useRequestLimits({ cachedClientLimit: clientContext.quinnWeeklyPromptLimit, clientIdOverride: clientContext.clientId });
  const user = React.useContext(UserContext);
  const userIsInOperationAdmin = React.useRef(user.roles.includes(Roles.OPERATIONS_ADMIN)).current;

  React.useEffect(() => {
    clientService.getAllDatabaseConnections().then((dbc) => {
      setDbStringList(dbc);
    });
  }, []);

  React.useEffect(() => {    
    if (regulatorClients.length > 0) return;
    if (clientContext.clientType?.toLowerCase() !== ClientType.Issuer.toLowerCase()) return;
    if (!featureFlags.has(Features.TWOSIDED_ISSUER_WORKFLOW)) return;
    clientService.getRegulatorsList().then(setRegulatorClients);
  }, [clientContext.clientType]);

  const getContainerNameErrorText = (): string => {
    const value = clientContext.storageName;
    if (value.length < 3 || value.length > 63) {
      return "The Storage Container Name must be between 3 and 63 characters in length.";
    } else if (!value.match(/^[a-z]/)) {
      return "The Storage Container Name must start with a lowercase letter.";
    } else if (!value.match(/^[-a-z0-9]+$/)) {
      return "The Storage Container Name must only contain lowercase letters, numbers, and hyphens.";
    } else if (!!value.match(/(-{2,})|-$/)) {
      return "The Storage Container Name must not contain consecutive hyphens or end with a hyphen.";
    }
  }

  const containerNameValidation = React.useMemo(() => {
    return (
      <span>
        The Storage Container Name must:
        <ul className="text-start">
          <li>be between 3 and 63 characters in length</li>
          <li>start with a lowercase letter</li>
          <li>only contain lowercase letters, numbers, and hyphens</li>
          <li>not contain consecutive hyphens</li>
          <li>not end with a hyphen</li>
        </ul>
      </span>
    );
  }, []);

  const getOMActivationDateDisplayValue = (activationDate : string): string => {
    return activationDate && activationDate.length === 19
      ? activationDate.substring(0,10)
      : activationDate;
  }

  const regulatorOptions = React.useMemo(() => {
    const innerOptions: { [key: string]: MultiselectItem } = {};
    regulatorClients.forEach((c) => Object.assign(innerOptions,
      {
        [c.clientId]: {
          currentState: clientContext.regulatorClientIds?.includes(c.clientId) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          displayValue: c.name,
        }
      })
    );
    return innerOptions;
  }, [regulatorClients, clientContext.regulatorClientIds]);

  const updateRegulatorClientIds = (response: MultiselectOnChangeResponse) => {
    clientContext.setRegulatorClientIds(response.checkedKeys.map((key) => parseInt(key, 10)));
  }

  return (
    <>
      <QESTextInput
        containerClassName="col-8 mb-3 px-2"
        label="Name"
        testId="new-client__name"
        value={clientContext.clientName || ""}
        onChange={(e) => clientContext.setClientName(e.target.value)}
        disabled={props.disabled}
        required={true}
        isValid={clientContext.validationObject["clientName"]}
      />

      <QESTextInput
        containerClassName="col-8 mb-3 px-2"
        label="SalesForce Account ID"
        testId="new-client__sf-id"
        value={clientContext.sfId || ""}
        onChange={(e) => clientContext.setSfId(e.target.value)}
        disabled={props.disabled}
        isValid={clientContext.validationObject["sfId"]}
      />

      <QESSelect
        containerClassName="col-12 mb-3 px-2"
        label="Database Connection"
        testId="new-client__db-string"
        value={clientContext.dbConnection}
        onChange={(e) => clientContext.setDbConnection(parseInt(e.target.value, 10))}
        disabled={props.disabled}
        required={true}
        isValid={clientContext.validationObject["dbConnection"]}
      >
        <option key={0} value={0}>{"Select a database connection:"}</option>
        {dbStringList.map((s) => <option key={s.connectionId} value={s.connectionId}>{`${s.connectionDescription} (${s.connectionString})`}</option>)}
      </QESSelect>

      <QESSelect
        containerClassName="col-12 mb-3 px-2"
        label="Cross-Project Database Connection"
        testId="new-client__cross-project-db-string"
        value={clientContext.crossProjectDbConnection}
        onChange={(e) => clientContext.setCrossProjectDbConnection(parseInt(e.target.value, 10))}
        disabled={props.disabled}
        required={true}
        isValid={clientContext.validationObject["crossProjectDbConnection"]}
      >
        <option key={0} value={0}>{"Select a database connection:"}</option>
        {dbStringList.map((s) => <option key={s.connectionId} value={s.connectionId}>{`${s.connectionDescription} (${s.connectionString})`}</option>)}
      </QESSelect>

      <QESSelect
          containerClassName="col-12 mb-3 px-2"
          label="Enterprise Compliance Dashboard Database Connection"
          testId="new-client__enterprise-compliance-dashboard-db-string"
          value={clientContext.enterpriseComplianceDashboardDbConnection}
          onChange={(e) => clientContext.setEnterpriseComplianceDashboardDbConnection(parseInt(e.target.value, 10))}
          disabled={props.disabled}
          required={true}
          isValid={clientContext.validationObject["enterpriseComplianceDashboardDbConnection"]}
        >
          <option key={0} value={0}>{"Select a database connection:"}</option>
          {dbStringList.map((s) => <option key={s.connectionId} value={s.connectionId}>{`${s.connectionDescription} (${s.connectionString})`}</option>)}
      </QESSelect>

      { <QESSelect
          containerClassName="col-12 mb-3 px-2"
          label="PCI Dashboard Database Connection"
          testId="new-client__pci-dashboard-db-string"
          value={clientContext.pciDashboardDbConnection}
          onChange={(e) => clientContext.setPciDashboardDbConnection(parseInt(e.target.value, 10))}
          disabled={props.disabled}
          required={true}
          isValid={clientContext.validationObject["pciDashboardDbConnection"]}
        >
          <option key={0} value={0}>{"Select a database connection:"}</option>
          {dbStringList.map((s) => <option key={s.connectionId} value={s.connectionId}>{`${s.connectionDescription} (${s.connectionString})`}</option>)}
        </QESSelect>
      }

      {clientContext.isEditingStorageConnectionString ? (
        <div className="col-12 mb-3 px-2 d-flex align-items-end">
          <QESTextInput
            containerClassName="flex-grow-1 me-2"
            label="Storage Connection String"
            testId="new-client__storage-string"
            value={clientContext.storageString || ""}
            onChange={(e) => clientContext.setStorageString(e.target.value)} disabled={props.disabled}
            required={true}
            isValid={clientContext.validationObject["storageString"]}
          />
          {clientContext.clientId !== undefined && (
            <QESButton
              qesTestId="new-client__storage-string-cancel"
              qesType="secondary"
              disabled={props.disabled}
              onClick={() => clientContext.cancelEditStorageConnectionString()}
            >
              Cancel
            </QESButton>
          )}
        </div>
      ) : (
        <div className="col-12 mb-3 px-2 d-flex align-items-end">
          <QESTextInput
            containerClassName="flex-grow-1 me-2"
            label="Storage Connection String"
            testId="new-client__storage-string-masked"
            value={clientContext.storageAccountName ? `Account: ${clientContext.storageAccountName} (key hidden)` : "(hidden)"}
            readOnly={true}
            disabled={props.disabled}
          />
          <QESButton
            qesTestId="new-client__storage-string-edit"
            qesType="secondary"
            disabled={props.disabled}
            onClick={() => clientContext.beginEditStorageConnectionString()}
          >
            Edit
          </QESButton>
        </div>
      )}

      <QESTextInput
        containerClassName="col-8 mb-3 px-2"
        label="Outreach Client XID"
        testId="new-client__outreach-client-xid"
        value={clientContext.outreachClientXid || ""}
        onChange={(e) => clientContext.setOutreachClientXid(e.target.value)}
        disabled={props.disabled}
        isValid={clientContext.validationObject["outreachClientXid"]}
      />

      <QESDateInput
        containerClassName="col-8 mb-3 px-2"
        label="Outreach Metrics Activation Date"
        testId="new-client__outreach-activation-date"
        value={getOMActivationDateDisplayValue(clientContext.outreachMetricsActivationDate)}
        onChange={(e) => clientContext.setOutreachMetricsActivationDate(e.target.value)}
        disabled={props.disabled}
        isValid={clientContext.validationObject["outreachClientActivationDate"]}
      />

      <QESTextInput
        containerClassName="col-8 mb-3 px-2"
        disabled={props.disabled}
        errorText={getContainerNameErrorText()}
        isValid={clientContext.validationObject["storageName"]}
        label="Storage Container Name"
        onChange={(e) => clientContext.setStorageName(e.target.value)}
        pattern="^(?=.{3,63}$)[a-z0-9]+(-[a-z0-9]+)*$"
        required={true}
        testId="new-client__storage-name"
        validationTooltipContent={containerNameValidation}
        value={clientContext.storageName || ""}
      />

      <QESTextInput
        containerClassName="col-9 mb-3 px-2" label="Logo URL" testId="new-client__logo-url" value={clientContext.logoUrl || ""} onChange={(e) => clientContext.setLogoUrl(e.target.value)} disabled={props.disabled} />

      <QESSelect
        containerClassName="col-8 mb-3 px-2"
        label="Volume Action Record Limit"
        testId="new-client__volume-targeting-record-limit"
        value={clientContext.bulkUploadRecordLimit}
        onChange={(e) => clientContext.setBulkUploadRecordLimit(parseInt(e.target.value))}
        disabled={props.disabled}
        isValid={clientContext.validationObject["bulkUploadRecordLimit"]}
        >
        {Array.from({ length: 10 }, (_, i) => (i + 1) * 10000)
          .concat(Array.from({length: 5}, (_, i) => ((i + 1) * 20000) + 100000)).map((s, i) => <option key={i} value={s}>{s}</option>)}
      </QESSelect>

      <QESSelect
        containerClassName="col-8 mb-3 px-2"
        label="Volume Action Limit Hour Window"
        testId="new-client__volume-targeting-limit-hour-window"
        value={clientContext.bulkUploadLimitHourWindow}
        onChange={(e) => clientContext.setBulkUploadLimitHourWindow(parseInt(e.target.value, 10))}
        disabled={props.disabled}
        isValid={clientContext.validationObject["bulkUploadLimitHourWindow"]}
        >
        {[8, 10, 12].map((s, i) => <option key={i} value={s}>{s}</option>)}
      </QESSelect>

      <QESSelect
        containerClassName="col-8 mb-3 px-2"
        label="Network Servicing Provider Report Record Limit"
        testId="new-client__network-provider-report-record-limit"
        value={clientContext.networkServicingProviderReportLimit}
        onChange={(e) => clientContext.setNetworkServicingProviderReportLimit(parseInt(e.target.value))}
        disabled={props.disabled}
        isValid={clientContext.validationObject["networkServicingProviderReportLimit"]}
      >
        {Array.from({ length: 10 }, (_, i) => (i + 1) * 200000).map((s, i) => <option key={i} value={s}>{s}</option>)}
      </QESSelect>

      <QESSelect
        containerClassName="col-8 mb-3 px-2"
        label="Prospect/Market Servicing Provider Report Record Limit"
        testId="new-client__prospect-market-provider-report-record-limit"
        value={clientContext.prospectMarketServicingProviderReportLimit}
        onChange={(e) => clientContext.setProspectMarketServicingProviderReportLimit(parseInt(e.target.value))}
        disabled={props.disabled}
        isValid={clientContext.validationObject["prospectMarketServicingProviderReportLimit"]}
      >
        {Array.from({ length: 10 }, (_, i) => (i + 1) * 200000).map((s, i) => <option key={i} value={s}>{s}</option>)}
      </QESSelect>

      {(featureFlags.has(Features.AI_VIRTUAL_ASSISTANT) || featureFlags.has(Features.AI_VIRTUAL_ASSISTANT_GA)) && (
       <>
         <div className="d-flex align-items-end col-8 mb-3 px-2">
           <QESTextInput
             containerClassName="flex-grow-1"
             label="Quinn Weekly Prompt Limit"
             testId="new-client__quinn-weekly-prompt-limit"
             type="number"
             value={clientContext.quinnWeeklyPromptLimit?.toString() ?? DEFAULT_QUINN_WEEKLY_PROMPT_LIMIT.toString()}
             onChange={(e) => clientContext.setQuinnWeeklyPromptLimit(parseInt(e.target.value, 10) || 0)}
             disabled={props.disabled || clientContext.quinnWeeklyPromptLimit === UNLIMITED_WEEKLY_QUINN_LIMIT}
             isValid={clientContext.validationObject["quinnWeeklyPromptLimit"]}
           />
           <QESCheckbox
             containerClassName="ms-3 mb-1"
             label="Unlimited"
             testId="new-client__quinn-weekly-prompt-unlimited"
             checked={clientContext.quinnWeeklyPromptLimit === UNLIMITED_WEEKLY_QUINN_LIMIT}
             onChange={(e) => {
               if (e.target.checked) {
                 clientContext.setQuinnWeeklyPromptLimit(UNLIMITED_WEEKLY_QUINN_LIMIT);
               } else {
                 clientContext.setQuinnWeeklyPromptLimit(DEFAULT_QUINN_WEEKLY_PROMPT_LIMIT);
               }
             }}
             disabled={props.disabled}
           />
         </div>

         {clientContext.clientId && clientRequests !== null && (
           <div className="col-8 mb-3 d-flex px-2">
             <p>Quinn Weekly Prompts Used / Available:</p>
             <p className="ms-3"><strong>{clientRequests} / {clientContext.quinnWeeklyPromptLimit}</strong></p>
           </div>
         )}

       </>
      )}

      <QESSelect
        containerClassName="col-8 mb-3 px-2"
        label="Competitive Insights Data Rights"
        testId="new-client__network-benchmarking-data-rights"
        value={clientContext.networkBenchmarkingDataRights || ""}
        onChange={(x) => clientContext.setNetworkBenchmarkingDataRights(x.target.value as ClientNetworkBenchmarkingDataRightsEnum)}
        disabled={true}
      >
        {Object.values(ClientNetworkBenchmarkingDataRightsEnum).map((x, index) => <option key={index} value={x}>{x}</option>)}
      </QESSelect>

      {clientContext.clientId !== GlobalConstants.QuestClientId ? (
        <>
          <QESCheckbox containerClassName="col-12  px-2" label='Resource Admin Guest Logins Allowed' testId="new-client__resource_admin_guest_logins_allowed" onChange={(e) => clientContext.setResourceAdminGuestLoginsAllowed(e.target.checked)} checked={clientContext.resourceAdminGuestLoginsAllowed} />
          <QESCheckbox containerClassName="col-12 mb-4 px-2" label='Quest Viewer Guest Logins Allowed' testId="new-client__quest_viewer_guest_logins_allowed" onChange={(e) => clientContext.setQuestViewerGuestLoginsAllowed(e.target.checked)} checked={clientContext.questViewerGuestLoginsAllowed} />
        </>
      ) : null}

      <QESCheckbox containerClassName="col-12 mb-4 px-2" label='Quest Internal (allow internal-only roles)' testId="new-client__quest_internal" onChange={(e) => clientContext.setIsQuestInternal(e.target.checked)} checked={clientContext.isQuestInternal} />

      <div>
        <TooltipWrapper text='When enabled, a confirmation message is displayed during user creation to remind the user that this client has special rules related to creation of new users.'>
          <QESCheckbox
            containerClassName="col-12 mb-4 px-2"
            label='Enable "Restricted" User Creation'
            testId="new-client__restricted_user_creation_enabled"
            onChange={(e) => clientContext.setRestrictedUserCreationEnabled(e.target.checked)}
            checked={clientContext.restrictedUserCreationEnabled}
          />
        </TooltipWrapper>
      </div>

      {!!userIsInOperationAdmin &&
      <div>
        <TooltipWrapper text='These checkboxes have a major performance impact! Do not check these unless you know what you are doing!'>
          <QESCheckbox containerClassName="col-12 px-2" label='Disable Access Cache Reads' testId="new-client__disable_access_cache_reads" onChange={(e) => clientContext.setDisableAccessCacheReads(e.target.checked)} checked={clientContext.disableAccessCacheReads} />
          <QESCheckbox containerClassName="col-12 mb-4 px-2" label='Disable Access Cache Writes' testId="new-client__disable_access_cache_writes" onChange={(e) => clientContext.setDisableAccessCacheWrites(e.target.checked)} checked={clientContext.disableAccessCacheWrites} />
        </TooltipWrapper>
      </div>}

      {!!userIsInOperationAdmin && featureFlags.has(Features.TERMINATION_SKIP_TIME_DISTANCE_RECALC) &&
      <div>
        <TooltipWrapper text="Skips termination time/distance recalculation for provider changes jobs queued without prompting.">
          <QESCheckbox 
            containerClassName="col-12 mb-4 px-2"
            label="Skip Termination Time/Distance Recalc Default Value"
            testId="new-client__skip_termination_recalc_default_value" 
            onChange={(e) => clientContext.setSkipTerminationTimeDistanceRecalcDefaultValue(e.target.checked)} 
            checked={clientContext.skipTerminationTimeDistanceRecalcDefaultValue} 
            disabled={props.disabled} 
          />
        </TooltipWrapper>
      </div>}

      <div>
        <QESSelect
          containerClassName="col-8 mb-3 px-2"
          label={"Client Type"}
          testId={"new-client__client-type"}
          onChange={(e) => {
            clientContext.setClientType(e.target.value as ClientType);
            clientContext.setRegulatorClientIds([]);
          }}
          value={clientContext.clientType.toLowerCase()}
        >
          <option value={ClientType.Issuer.toLowerCase()} key={ClientType.Issuer.toLowerCase()}>Issuer</option>
          <option value={ClientType.Regulator.toLowerCase()} key={ClientType.Regulator.toLowerCase()}>Regulator</option>
          <option value={ClientType.QuestInternal.toLowerCase()} key={ClientType.QuestInternal.toLowerCase()}>Quest Internal</option>
          {clientContext.isTwoSidedRegulatorEligible && <option value={ClientType.TwoSidedRegulator.toLowerCase()} key={ClientType.TwoSidedRegulator.toLowerCase()}>Two-Sided Regulator</option>}
          {clientContext.showInternalClientResource && (
            <option value={ClientType.InternalClientResourceProcessor.toLowerCase()} key={ClientType.InternalClientResourceProcessor.toLowerCase()}>Internal Client Resource Processor</option>
          )}
        </QESSelect>
        {featureFlags.has(Features.TWOSIDED_ISSUER_WORKFLOW) && clientContext.clientType?.toLowerCase() === ClientType.Issuer.toLowerCase() && (
          <QESMultiSelect
            containerClassName="col-8 mb-3 px-2"
            disabled={props.disabled}
            label="Compliance Connect Regulators"
            onChange={updateRegulatorClientIds}
            options={regulatorOptions}
            prefix="regulatorClientIds"
            testId="new-client__compliance-connect-regulator"
          />
        )}
      </div>
    </>
  );
}
